import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, Stack, Switch, useTheme } from "@mui/material";
import { Button, FoodsDeleteButton, FoodsIcon, FoodsSelect, ToggleSwitch, Typography } from "@foodpilot/foods";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetAllPackaging } from "src/api/client-api/packagings/useGetAllPackagings";
import { useUpsertPackagingComposite } from "src/api/client-api/packagings/useUpsertPackagingComposite";
import { toPackagingShareWrites } from "src/api/client-api/packagings/useUpsertPackaging";
import { useCalculatePackagingPreview } from "src/api/client-api/packagings/useCalculatePackagingPreview";
import type { Packaging } from "src/api/client-api/packagings/useGetOnePackaging";
import { usePackagingReferenceComposite } from "src/api/sure-api/usePackagingReferenceComposite";
import { Loading } from "src/components/utils/Loading";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { compositeEditorFormSchema, compositeSchema, type CompositeEditorFormData } from "./compositeSchema";
import { FormCardSection } from "../_shell/FormCardSection";
import { PackagingFormLayout } from "../_shell/PackagingFormLayout";
import { FieldError, FieldStack, RhfFoodsTextField } from "../_shell/RhfFoodsFields";
import { AssociatedOrganizationsField } from "../Element/AssociatedOrganizationsField";
import { CompositeCompositionRow } from "./CompositeCompositionRow";
import { getCandidateScore } from "./compositeScore";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";
import { EntityValidationBanner } from "src/components/EntityForm/EntityValidationBanner";
import { EntityUnsavedChangesDialog } from "src/components/EntityForm/EntityUnsavedChangesDialog";
import { useEntityFormLeaveGuard } from "src/components/EntityForm/useEntityFormLeaveGuard";

type Props = {
    existing?: Packaging;
};

type ReferenceOption = {
    id: number;
    label: string;
    defaultWeight: number | null;
    components: Array<{
        packagingReferenceId: number;
        label: string;
        defaultWeight: number | null;
        amount: number;
        position: number;
        isMain: boolean;
    }>;
};

export const CreatePackagingComposite = ({ existing }: Props = {}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentCampaign, selectedCompany } = useFoodpilotContext();
    const { packagings, isLoading: isPackagingsLoading } = useGetAllPackaging({ fetchAllPages: true });
    const { packagingReferenceComposites, isLoading: isReferencesLoading } = usePackagingReferenceComposite();
    const upsert = useUpsertPackagingComposite();
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [validationAttempt, setValidationAttempt] = useState(0);
    const validationBannerRef = useRef<HTMLDivElement>(null);
    const saveForExitRef = useRef(false);
    const submitInFlightRef = useRef(false);

    const isEdit = existing !== undefined;
    const initialReferenceId = existing?.packagingReferenceCompositeId ?? null;

    const sortedReferences = useMemo<ReferenceOption[]>(
        () => packagingReferenceComposites.toSorted((a, b) => a.label.localeCompare(b.label)),
        [packagingReferenceComposites],
    );
    const candidateChildren = useMemo(
        () =>
            (packagings ?? [])
                .filter((packaging) => existing === undefined || packaging.id !== existing.id)
                .toSorted((a, b) => a.name.localeCompare(b.name)),
        [existing, packagings],
    );

    const defaultValues: CompositeEditorFormData = useMemo(() => {
        const fallbackComposition = [{ childId: 0, amount: 1, isMain: true }];

        if (existing === undefined) {
            return {
                mode: "specific",
                name: "",
                externalId: "",
                packagingReferenceCompositeId: null,
                composition: fallbackComposition,
                shares: [],
            };
        }

        const composition =
            existing.composition !== undefined && existing.composition.length > 0 ?
                existing.composition
                    .toSorted((a, b) => a.position - b.position)
                    .map((row, index) => ({
                        id: row.id,
                        childId: row.child.id,
                        amount: row.amount,
                        isMain: row.isMain ?? index === 0,
                    }))
            :   fallbackComposition;

        return {
            mode: initialReferenceId === null ? "specific" : "generic",
            name: existing.name,
            externalId: existing.externalId ?? "",
            packagingReferenceCompositeId: initialReferenceId,
            composition:
                composition.some((row) => row.isMain) ? composition : (
                    composition.map((row, index) => ({ ...row, isMain: index === 0 }))
                ),
            shares: existing.shares?.map((share) => share.organization) ?? [],
        };
    }, [existing, initialReferenceId]);

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isDirty, isSubmitted },
    } = useForm<CompositeEditorFormData>({
        resolver: zodResolver(compositeEditorFormSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues,
    });

    useEffect(() => {
        if (validationAttempt === 0) return;
        validationBannerRef.current?.focus({ preventScroll: true });
        validationBannerRef.current?.scrollIntoView?.({ block: "start" });
    }, [validationAttempt]);

    const { fields, append, remove } = useFieldArray({
        control,
        name: "composition",
        keyName: "fieldArrayId",
    });

    const mode = watch("mode");
    const useGenericReference = mode === "generic";
    const selectedReferenceId = watch("packagingReferenceCompositeId");
    const selectedReference =
        selectedReferenceId !== null ? (sortedReferences.find((ref) => ref.id === selectedReferenceId) ?? null) : null;
    const compositionRows = watch("composition");
    const listUrl = URLHandler.packaging.list(selectedCompany?.id ?? 0);
    const leaveGuard = useEntityFormLeaveGuard({ dirty: isDirty, busy: upsert.isLoading });
    const mainIndex = Math.max(
        0,
        compositionRows.findIndex((row) => row.isMain),
    );
    const mainRow = compositionRows[mainIndex];
    const isSpecificModeInvalid =
        !useGenericReference &&
        (candidateChildren.length === 0 ||
            compositionRows.length === 0 ||
            compositionRows.filter((row) => row.isMain).length !== 1 ||
            compositionRows.some((row) => row.childId <= 0 || row.amount <= 0));
    const preview = useCalculatePackagingPreview(
        useGenericReference ?
            {
                type: "composite",
                packagingReferenceCompositeId: selectedReferenceId,
                weight: selectedReference?.defaultWeight ?? null,
            }
        :   {
                type: "composite",
                composition: compositionRows.reduce<
                    Array<{ childPackagingId: number; amount: number; isMain: boolean }>
                >(
                    (rows, row) =>
                        row.childId > 0 && row.amount > 0 ?
                            [...rows, { childPackagingId: row.childId, amount: row.amount, isMain: row.isMain }]
                        :   rows,
                    [],
                ),
            },
        {
            enabled: useGenericReference ? selectedReferenceId !== null : !isSpecificModeInvalid,
        },
    );

    if (selectedCompany === null || currentCampaign === null) {
        return null;
    }

    if (isPackagingsLoading || isReferencesLoading) {
        return <Loading appearance={appearance} />;
    }

    const onCancel = () => leaveGuard.requestExit(listUrl);

    const onSubmit = (data: CompositeEditorFormData) => {
        if (submitInFlightRef.current) return;
        setSubmitError(null);

        const formPayload =
            data.mode === "generic" && data.packagingReferenceCompositeId !== null ?
                {
                    mode: "generic" as const,
                    name: data.name,
                    externalId: data.externalId,
                    packagingReferenceCompositeId: data.packagingReferenceCompositeId,
                }
            :   {
                    mode: "specific" as const,
                    name: data.name,
                    externalId: data.externalId,
                    composition: data.composition,
                };
        const validation = compositeSchema.safeParse(formPayload);

        if (!validation.success) {
            setSubmitError(t("packaging_composite.validation_error", "Please complete the required fields."));
            setValidationAttempt((current) => current + 1);
            return;
        }

        submitInFlightRef.current = true;
        upsert.mutate(
            {
                organizationId: selectedCompany.id,
                campaignId: currentCampaign.id,
                packaging:
                    validation.data.mode === "generic" ?
                        {
                            ...(isEdit ? { id: existing.id } : {}),
                            name: validation.data.name,
                            externalId: validation.data.externalId || null,
                            packagingReferenceCompositeId: validation.data.packagingReferenceCompositeId,
                            expandFromReference: !isEdit,
                            composition: [],
                            weight: selectedReference?.defaultWeight ?? existing?.weight ?? null,
                            shares: toPackagingShareWrites(data.shares ?? []),
                        }
                    :   {
                            ...(isEdit ? { id: existing.id } : {}),
                            name: validation.data.name,
                            externalId: validation.data.externalId || null,
                            packagingReferenceCompositeId: null,
                            composition: validation.data.composition.map((row, index) => {
                                const existingCompositionId = data.composition[index]?.id;

                                return {
                                    ...(existingCompositionId === undefined ?
                                        {}
                                    :   {
                                            "@id": `/api/packaging-compositions/${existingCompositionId}` as IRI,
                                        }),
                                    child: `/api/packagings/${row.childId}` as IRI,
                                    amount: row.amount,
                                    position: index,
                                    isMain: row.isMain,
                                };
                            }),
                            shares: toPackagingShareWrites(data.shares ?? []),
                        },
            },
            {
                onSuccess: () => {
                    saveForExitRef.current = false;
                    leaveGuard.completeSave(listUrl);
                },
                onError: (err: unknown) => {
                    setSubmitError(err instanceof Error ? err.message : String(err));
                    if (saveForExitRef.current) {
                        saveForExitRef.current = false;
                        leaveGuard.cancelSave();
                    }
                },
                onSettled: () => {
                    submitInFlightRef.current = false;
                },
            },
        );
    };

    const submit = handleSubmit(onSubmit, () => {
        setValidationAttempt((current) => current + 1);
        if (saveForExitRef.current) {
            saveForExitRef.current = false;
            leaveGuard.cancelSave();
        }
    });
    const addAssociatedElement = () => append({ childId: 0, amount: 1, isMain: false });
    const genericDataLabel = t(
        "packaging_composite.reference.use_generic_data",
        "Use reference generic data of this packaging",
    );
    const setGenericReferenceMode = (checked: boolean) => {
        setValue("mode", checked ? "generic" : "specific", {
            shouldDirty: true,
            shouldValidate: isSubmitted,
        });
        if (checked && selectedReference !== null) {
            setValue("name", selectedReference.label, {
                shouldDirty: true,
                shouldValidate: isSubmitted,
            });
        }
    };
    const genericReferenceLabel = t("packaging_composite.reference.label", "Associated reference packaging");
    const genericReferenceSelect = (
        <FoodsSelect<ReferenceOption>
            selectedOption={selectedReference}
            options={sortedReferences}
            getId={(option) => option.id}
            getName={(option) => option.label}
            onChange={(value) => {
                setValue("packagingReferenceCompositeId", value?.id ?? null, {
                    shouldDirty: true,
                    shouldValidate: isSubmitted,
                });
                if (value !== null && useGenericReference) {
                    setValue("name", value.label, { shouldDirty: true, shouldValidate: isSubmitted });
                }
            }}
            textOptions={{
                placeholder: t("packaging_composite.reference.placeholder", "Select a reference"),
                searchPlaceholder: t("packaging_composite.reference.search", "Search a reference..."),
                emptyOptionsText: t("packaging_composite.reference.empty", "No reference available"),
                noResult: t("packaging_composite.reference.no_result", "No reference found"),
            }}
            BoxProps={{ width: "100%" }}
            BoxOptionsProps={{ width: theme.spacing(56.25) }}
        />
    );

    return (
        <>
            <PackagingFormLayout
                appearance={appearance}
                title={
                    isEdit ?
                        t("packaging_composite.edit.title", "Edit packaging unit")
                    :   t("packaging_composite.create.title", "New packaging unit")
                }
                activeBreadcrumb={
                    isEdit ?
                        t("packaging_composite.edit.breadcrumb", "Edit packaging unit")
                    :   t("packaging_composite.create.breadcrumb", "New packaging unit")
                }
                companyName={selectedCompany.name}
                onCancel={onCancel}
                onCancelLabel={t("cancel", "Cancel")}
                onSaveLabel={t("save", "Save")}
                onSubmit={() => void submit()}
                isSaving={upsert.isLoading}
                packaging={existing}
                previewScores={preview.data}
                isPreviewLoading={preview.isFetching}
            >
                <EntityValidationBanner
                    bannerRef={validationBannerRef}
                    entityLabel={t("entity_form.entity.packaging_unit", "packaging unit")}
                    issueCount={Object.keys(errors).length}
                />
                <FormCardSection
                    title={t("packaging_composite.reference.title", "1/4 • Reference packaging")}
                    description={t(
                        "packaging_composite.reference.description",
                        "Associate your packaging to a reference. You can choose to use generic data of this reference instead of custom specific data.",
                    )}
                    bodyGap={12}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                        <Typography variant="body-medium" sx={{ color: theme.custom.grey[2800] }}>
                            {genericDataLabel}
                        </Typography>
                        {isProductUxV2 ?
                            <ToggleSwitch
                                checked={useGenericReference}
                                disabled={isEdit}
                                inputProps={{ "aria-label": genericDataLabel, role: "switch" }}
                                onChange={(event) => setGenericReferenceMode(event.target.checked)}
                            />
                        :   <Switch
                                checked={useGenericReference}
                                disabled={isEdit}
                                inputProps={{ "aria-label": genericDataLabel }}
                                onChange={(event) => setGenericReferenceMode(event.target.checked)}
                            />
                        }
                    </Stack>
                    {useGenericReference && (
                        <FieldStack label={genericReferenceLabel} required={isProductUxV2}>
                            {isProductUxV2 ?
                                <FoodsSelectTriggerAdapter accessibleLabel={`${genericReferenceLabel}*`}>
                                    {genericReferenceSelect}
                                </FoodsSelectTriggerAdapter>
                            :   genericReferenceSelect}
                            {errors.packagingReferenceCompositeId?.message && (
                                <FieldError
                                    message={t("form.required_field", "This field is required.")}
                                    announceError={isProductUxV2}
                                />
                            )}
                        </FieldStack>
                    )}
                    {useGenericReference && selectedReference?.components.length ?
                        <Stack spacing={0.5}>
                            <Typography variant="body-medium" sx={{ color: theme.custom.grey[2800] }}>
                                {t("packaging_composite.reference.breakdown", "Reference breakdown")}
                            </Typography>
                            {selectedReference.components
                                .toSorted((a, b) => a.position - b.position)
                                .map((component) => (
                                    <Typography
                                        key={`${component.packagingReferenceId}-${component.position}`}
                                        variant="body2"
                                        sx={{ color: theme.custom.grey[2200] }}
                                    >
                                        {component.label} × {component.amount}
                                    </Typography>
                                ))}
                        </Stack>
                    :   null}
                </FormCardSection>

                <FormCardSection
                    title={t("packaging_composite.identity.title", "2/4 • Identity")}
                    description={t(
                        "packaging_composite.identity.description",
                        "Information needed to identify your packaging unit.",
                    )}
                    bodyGap={24}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} gap={3}>
                        <Box flex={1} minWidth={0}>
                            <RhfFoodsTextField
                                control={control}
                                name="name"
                                label={t("packagings.name", "Packaging name")}
                                required
                                errorMessage={
                                    errors.name?.message === "form.required_field" ?
                                        t("form.required_field", "This field is required.")
                                    :   errors.name?.message
                                }
                                disabled={useGenericReference && selectedReference !== null}
                            />
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <RhfFoodsTextField
                                control={control}
                                name="externalId"
                                label={t("unique_identifier", "Unique identifier")}
                                disabled={useGenericReference && selectedReference !== null}
                            />
                        </Box>
                    </Stack>
                    <Controller
                        name="shares"
                        control={control}
                        render={({ field }) => (
                            <AssociatedOrganizationsField
                                appearance={appearance}
                                value={field.value ?? []}
                                onChange={field.onChange}
                                sharesElements
                            />
                        )}
                    />
                </FormCardSection>

                {!useGenericReference && (
                    <>
                        <FormCardSection
                            title={t("packaging_composite.main_element.title", "3/4 • Main element")}
                            description={t(
                                "packaging_composite.main_element.description",
                                "Packaging component with the highest weight and its quantity.",
                            )}
                            bodyGap={16}
                        >
                            <CompositeCompositionRow
                                appearance={appearance}
                                control={control}
                                index={mainIndex}
                                candidates={candidateChildren}
                                errors={errors}
                                score={getCandidateScore(candidateChildren, mainRow?.childId)}
                                tChild={t("packaging_composite.child", "Packaging component")}
                                tAmount={t("packaging_composite.amount", "Quantity")}
                            />
                        </FormCardSection>

                        <FormCardSection
                            title={t("packaging_composite.associated_elements.title", "4/4 • Associated elements")}
                            description={t(
                                "packaging_composite.associated_elements.description",
                                "Other components linked to the main element and their quantity.",
                            )}
                            bodyGap={16}
                        >
                            {fields.filter((_field, index) => index !== mainIndex).length === 0 ?
                                <Box
                                    sx={{
                                        p: 3,
                                        border: `${theme.custom.strokes.containerDivider.width} dashed ${theme.custom.grey[1000]}`,
                                        borderRadius: theme.spacing(1),
                                        backgroundColor: theme.palette.primary[50],
                                        textAlign: "center",
                                    }}
                                >
                                    <Stack spacing={2} alignItems="center">
                                        <Typography variant="body2" sx={{ color: theme.custom.grey[2800] }}>
                                            {t(
                                                "packaging_composite.associated_elements.empty",
                                                "Add an element to your packaging.",
                                            )}
                                        </Typography>
                                        <Button
                                            onClick={addAssociatedElement}
                                            startIcon={isProductUxV2 ? <FoodsIcon icon="plus" size={2} /> : <AddIcon />}
                                        >
                                            {t("packaging_composite.add_row", "Add an element")}
                                        </Button>
                                    </Stack>
                                </Box>
                            :   fields.map((field, index) =>
                                    index === mainIndex ? null : (
                                        <Stack
                                            key={field.fieldArrayId}
                                            direction="row"
                                            spacing={2}
                                            alignItems="flex-end"
                                        >
                                            <CompositeCompositionRow
                                                appearance={appearance}
                                                control={control}
                                                index={index}
                                                candidates={candidateChildren}
                                                errors={errors}
                                                tChild={t("packaging_composite.child", "Packaging component")}
                                                tAmount={t("packaging_composite.amount", "Quantity")}
                                            />
                                            {isProductUxV2 ?
                                                <FoodsDeleteButton
                                                    ariaLabel={t("packaging_composite.remove", "Remove row")}
                                                    onClick={() => remove(index)}
                                                    buttonProps={{ type: "button" }}
                                                    tooltip={{ text: t("common.actions.delete", "Delete") }}
                                                />
                                            :   <IconButton
                                                    aria-label={t("packaging_composite.remove", "Remove row")}
                                                    onClick={() => remove(index)}
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            }
                                        </Stack>
                                    ),
                                )
                            }
                            {fields.filter((_field, index) => index !== mainIndex).length > 0 && (
                                <Button
                                    onClick={addAssociatedElement}
                                    startIcon={isProductUxV2 ? <FoodsIcon icon="plus" size={2} /> : <AddIcon />}
                                >
                                    {t("packaging_composite.add_row", "Add an element")}
                                </Button>
                            )}
                        </FormCardSection>
                    </>
                )}

                {submitError && (
                    <Typography color="error" variant="body2">
                        {submitError}
                    </Typography>
                )}
            </PackagingFormLayout>
            <EntityUnsavedChangesDialog
                open={leaveGuard.open}
                busy={upsert.isLoading}
                onSave={() => {
                    saveForExitRef.current = true;
                    leaveGuard.beginSave();
                    void submit();
                }}
                onDiscard={() => {
                    saveForExitRef.current = false;
                    leaveGuard.discard();
                }}
                onKeepEditing={leaveGuard.keepEditing}
            />
        </>
    );
};
